import React, {useEffect} from 'react';
import MainLayoutIgniter from "../common/mainLayout";
import {Heading2, Heading3, Text4} from "../../../libs/uiKit/globalStyledComponents";
import {
    StyledAgreementNumber,
    StyledContractHeader, StyledContractRegistrationForm,
    StyledContractWrapper,
    StyledEventsButtons,
    StyledHeaderInformation,
    StyledHeaderWrapper,
    StyledImportantNumber,
    StyledInformationItem,
    StyledTotalInformation,
    StyledTotalInformationItem
} from "../contract/ui/styles";
import {FormProvider, useForm} from "react-hook-form";
import {ContractForm} from "../contract";
import Button from "../../../libs/uiKit/button";
import FlyButtons from "../../../libs/uiKit/flyButtons";
import LoadingString from "../../../libs/uiKit/loadingString";
import Status from "../../../libs/uiKit/status";
import {useQuery} from "@apollo/react-hooks";
import {
    LoadContractQuery,
    LoadContractQueryVariables,
    LoadFlatContractQuery,
    LoadFlatContractQueryVariables
} from "../../../libs/api/graphqlTypes";
import {LOAD_CONTRACT, LOAD_FLAT_CONTRACT} from "../../../libs/api/queries";
import useNotification from "../../widgets/notifier/ui/hooks/useNitification";
import {AgreementId} from "../createAgreement/ui/context";
import {useNavigate, useParams} from "react-router-dom";
import {stringToMoney} from "../../../libs/uiKit/utils";
import ContragentIgniter from "../createAgreement/widgets/contragent";
import {formatAgreementDataToFormData, formatPersonDataToDataForm} from "../createAgreement/ds/repositories/commands";
import FlatStep from "../createAgreement/pages/flat";
import {useGlobalContext} from "../../../apps/main/ui/GlobalContext";
import {IForm} from "../../../libs/uiKit/fieldsBuilder/types";
import dayjs from "dayjs";
import {FieldsBuilder} from "../../../libs/uiKit/fieldsBuilder";

type InformationItem = { title: string; description: React.ReactNode };


const FlatContractPage = () => {

    const formHook = useForm({ mode: 'onBlur' });

    const { setNotification } = useNotification();
    const navigate = useNavigate();

    const { id } = useParams<{ id?: string }>();
    const contractId: AgreementId = Number.isNaN(Number(id)) ? null : Number(id);

    const {
        routes: { invoiceById },
    } = useGlobalContext();

    const { data: dataContract, loading: loadingContract } = useQuery<LoadFlatContractQuery, LoadFlatContractQueryVariables>(LOAD_FLAT_CONTRACT, {
        variables: { id: contractId },
        onError: error => setNotification({ text: error, type: 'error' }),
        fetchPolicy: 'network-only',
    });

    const {
        status,
        added,
        number,
        user,
        calcForm,
        flatPremiumSum,
        agreement
    } = dataContract?.loadAgreement || {};


    useEffect(() => {
        if(!dataContract) return;
        const formData = formatAgreementDataToFormData(dataContract);
        Object.entries(formData).forEach(([key, value]) => {
            if (value === null || value === undefined) return;
            formHook.setValue(key, value);
        });
    }, [calcForm]);

    const isPayed = agreement?.is_payed || false;

    const policyholder = formatPersonDataToDataForm(calcForm?.policyholder || {});

    const dateBeg = calcForm?.flat?.date_begin;
    const dateEnd: string = dateBeg ? dayjs(dateBeg, 'DD.MM.YYYY').add(1, 'year').subtract(1, 'day').format('DD.MM.YYYY') : '';


    const goToInvoiceHandler = () => {
        navigate(invoiceById(String(contractId)));
    };

    const informationItems: InformationItem[] = [
        {
            title: 'Номер договора',
            description: (
                <StyledAgreementNumber>
                    <Text4>{number}</Text4>
                </StyledAgreementNumber>
            ),
        },
        {
            title: 'Статус подписания',
            description: (
                <LoadingString isLoading={loadingContract}>
                    <Status status={status?.label} color={status?.color}/>
                </LoadingString>
            ),
        },
        {
            title: 'Статус оплаты',
            description: (
                <LoadingString isLoading={loadingContract}>
                    {isPayed ? <Status status='Оплачен' color='#397e2d'/> : <Status status='Не оплачен' color='#d23630'/>}
                </LoadingString>
            ),
        },
        {
            title: 'Создан',
            description: (
                <LoadingString isLoading={loadingContract}>
                    <Text4>{added}</Text4>
                </LoadingString>
            ),
        }
    ];

    const totalInformationItems: InformationItem[] = [
        {
            title: 'Итого',
            description: (
                <LoadingString isLoading={loadingContract}>
                    <Text4>{stringToMoney(flatPremiumSum)} RUB</Text4>
                </LoadingString>
            ),
        }
    ];

    const form3: IForm = {
        formHook,
        hotReload: true,
        fields: [
            {
                field: {
                    fieldType: 'date',
                    fieldName: 'flatDateBeg',
                    isRequired: true,
                    placeholder: 'Дата начала',
                    validationType: 'minMaxDate',
                    grid: 3,
                    isDisabled: true,
                    calendarDirection: "up"
                },
            },
            {
                field: {
                    fieldType: 'input',
                    fieldName: 'flatDateEnd',
                    placeholder: 'Дата окончания',
                    isDisabled: true,
                    defaultValue: dateEnd,
                    grid: 3,
                },
            },
        ],
    }

    return (
        <MainLayoutIgniter>
            <Heading2>Договор страхования</Heading2>
            <StyledContractWrapper>
                <FormProvider {...formHook}>
                    <StyledHeaderWrapper>
                        <StyledContractHeader>
                            <Heading3>Общие сведения</Heading3>
                            <StyledEventsButtons>
                                <FlyButtons isWhiteBackground={true}>
                                    <Button appearance='transparent' onClick={goToInvoiceHandler}>
                                        Документы и оплата
                                    </Button>
                                </FlyButtons>
                            </StyledEventsButtons>
                        </StyledContractHeader>
                        <StyledHeaderInformation>
                            {informationItems.map(({ title, description }, index) => (
                                <StyledInformationItem key={`info-item-${index}`}>
                                    <Text4>{title}</Text4>
                                    {description}
                                </StyledInformationItem>
                            ))}
                            <StyledTotalInformation>
                                {totalInformationItems.map(({ title, description }, index) => (
                                    <StyledTotalInformationItem key={`info-item-${index}`}>
                                        <Text4>{title}</Text4>
                                        {description}
                                    </StyledTotalInformationItem>
                                ))}
                            </StyledTotalInformation>
                        </StyledHeaderInformation>
                    </StyledHeaderWrapper>
                    <StyledContractRegistrationForm>
                        <div style={{
                            fontSize: '1.4rem',
                            margin: '3rem 0 1.5rem',
                        }}>Период действия</div>
                        <FieldsBuilder {...form3} />
                        <Heading3>Страхователь</Heading3>
                        <ContragentIgniter
                            isShowShare={false}
                            isShowSearchByIsn={false}
                            number={0}
                            arrayName='contragent'
                            searchSuccessCallback={(contragent, number) => {}}
                            contragentDisabled={true}
                            postfix='страхователя'
                            defaultValues={policyholder}
                            flatMode={true}
                        />
                        <FlatStep isFieldsDisabled={true} isContract={true}/>
                    </StyledContractRegistrationForm>

                </FormProvider>
            </StyledContractWrapper>
        </MainLayoutIgniter>
    );
}

export default FlatContractPage;